/**
 * Program capacity and waitlist (BHCS-124) — thin wrappers over the SECURITY DEFINER RPCs.
 *
 * The database owns every rule: `enforce_program_capacity` refuses a parent's enrollment into a
 * full program, and each waitlist state change (join, withdraw, offer, take back) is an RPC that
 * checks who may make it and from which status. Nothing here decides anything; it reads counts
 * and asks for transitions. `program_waitlist` itself is read-only through the API.
 */
import { supabase } from '../lib/supabase';

export interface ProgramCapacityStatus {
  program_id: string;
  capacity: number;
  seats_taken: number;
  /** Seats an admin has offered to a waitlisted family and is holding for them. */
  seats_held: number;
  /** Never negative; an over-capacity program reads 0. */
  seats_left: number;
  waitlist_count: number;
}

export type WaitlistStatus = 'waiting' | 'offered' | 'enrolled' | 'withdrawn';

/** One of the signed-in parent's children in line (`program_waitlist_mine`). */
export interface MyWaitlistEntry {
  id: string;
  program_id: string;
  student_id: string;
  status: 'waiting' | 'offered';
  /** Place in line, 1 = next. Null once a seat is offered: the child is out of the line. */
  queue_position: number | null;
  created_at: string;
  offered_at: string | null;
}

/** An entry as the admin waitlist panel reads it, with the child and the parent who joined. */
export interface AdminWaitlistEntry {
  id: string;
  program_id: string;
  student_id: string;
  parent_id: string;
  status: WaitlistStatus;
  queue_seq: number;
  created_at: string;
  offered_at: string | null;
  offered_by: string | null;
  resolved_at: string | null;
  students: { first_name: string; last_name: string } | null;
  parents: { first_name: string | null; last_name: string | null; email: string } | null;
}

/** The error HINTs the capacity trigger and RPCs raise, for callers that branch on them. */
export const WAITLIST_HINT = {
  programFull: 'program_full',
  seatAvailable: 'seat_available',
  alreadyEnrolled: 'already_enrolled',
  notOpen: 'not_open',
} as const;

interface PostgrestLikeError {
  code?: string;
  hint?: string | null;
  message?: string;
}

/** Whether an enrollment write was refused because the program is full. */
export function isProgramFullError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as PostgrestLikeError;
  return e.hint === WAITLIST_HINT.programFull;
}

/** An RPC failure, keeping the database's HINT so a caller can tell the cases apart. */
export class WaitlistError extends Error {
  readonly hint: string | null;
  constructor(message: string, hint: string | null) {
    super(message);
    this.name = 'WaitlistError';
    this.hint = hint;
  }
}

function fail(error: PostgrestLikeError): never {
  throw new WaitlistError(error.message ?? 'Waitlist request failed', error.hint ?? null);
}

/**
 * Seat counts for the capped programs among `programIds`, keyed by program id. A program that is
 * missing from the map has no cap.
 */
export async function fetchProgramCapacity(
  programIds: string[],
): Promise<Map<string, ProgramCapacityStatus>> {
  const ids = [...new Set(programIds)].filter(Boolean);
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase.rpc('program_capacity_status', { p_program_ids: ids });
  if (error) fail(error);
  const rows = (data ?? []) as ProgramCapacityStatus[];
  return new Map(rows.map((row) => [row.program_id, row]));
}

/** The signed-in parent's children's live entries, for one program or all of them. */
export async function fetchMyWaitlistEntries(programId?: string): Promise<MyWaitlistEntry[]> {
  const { data, error } = await supabase.rpc('program_waitlist_mine', {
    p_program_id: programId ?? null,
  });
  if (error) fail(error);
  return (data ?? []) as MyWaitlistEntry[];
}

export interface JoinWaitlistResult {
  id: string;
  status: WaitlistStatus;
  position: number | null;
}

/** Put a child in line. Joining twice returns the existing entry. */
export async function joinWaitlist(
  programId: string,
  studentId: string,
  parentId?: string,
): Promise<JoinWaitlistResult> {
  const { data, error } = await supabase.rpc('join_program_waitlist', {
    p_program_id: programId,
    p_student_id: studentId,
    p_parent_id: parentId ?? null,
  });
  if (error) fail(error);
  return data as unknown as JoinWaitlistResult;
}

/** Leave the line (a parent), or remove a child from it (an admin). */
export async function withdrawFromWaitlist(entryId: string): Promise<void> {
  const { error } = await supabase.rpc('withdraw_from_waitlist', { p_entry_id: entryId });
  if (error) fail(error);
}

/** Admin: hold a seat for a waiting child and email the family. */
export async function offerWaitlistSeat(entryId: string): Promise<{ over_capacity: boolean }> {
  const { data, error } = await supabase.rpc('offer_waitlist_seat', { p_entry_id: entryId });
  if (error) fail(error);
  const result = data as unknown as { over_capacity?: boolean } | null;
  return { over_capacity: result?.over_capacity === true };
}

/** Admin: take an offer back; the child returns to their original place in line. */
export async function rescindWaitlistOffer(entryId: string): Promise<void> {
  const { error } = await supabase.rpc('rescind_waitlist_offer', { p_entry_id: entryId });
  if (error) fail(error);
}

/** Admin: the live line for one program, in order, offered entries included. */
export async function fetchProgramWaitlist(programId: string): Promise<AdminWaitlistEntry[]> {
  const { data, error } = await supabase
    .from('program_waitlist')
    .select(
      'id, program_id, student_id, parent_id, status, queue_seq, created_at, offered_at, offered_by, resolved_at, students ( first_name, last_name ), parents ( first_name, last_name, email )',
    )
    .eq('program_id', programId)
    .in('status', ['waiting', 'offered'])
    .order('queue_seq', { ascending: true });
  if (error) fail(error);
  return (data ?? []) as unknown as AdminWaitlistEntry[];
}

/** Whether a capped program has no seat for a family that holds no offer. */
export function isProgramFull(status: ProgramCapacityStatus | undefined): boolean {
  return !!status && status.seats_left <= 0;
}

/** "8 / 10" — seats taken (held seats included) of capacity. */
export function describeSeats(status: ProgramCapacityStatus): string {
  return `${status.seats_taken + status.seats_held} / ${status.capacity}`;
}

/**
 * The admin form's Capacity field: blank = unlimited, otherwise a whole number of seats ≥ 1.
 * Mirrors programs_capacity_check, so the form says what is wrong before the database does.
 */
export function parseCapacityInput(
  raw: string,
): { ok: true; value: number | null } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) {
    return { ok: false, error: 'Capacity must be a whole number of seats (1 or more), or blank for unlimited' };
  }
  return { ok: true, value: Number(trimmed) };
}
